import React, { useCallback, useState } from "react";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { API_get_flash_report } from "../../../../../constants";
import { downloadCSV } from "../../../../../utilities/csvExport";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  ReportScopeFilters,
  useCompanyReportScope,
  useScopedReportFetch,
} from "./_shared/companyReportScope";
import { formatCurrency } from "./_shared/laborMath";
import { Info, Switch } from "./_shared/reportControls";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

// Flash Report: one business day of Toast sales, discounts, voids, checks, and
// guests by location. The backend (store_info/services/flash_report.py) does
// the math, so this file only lays it out.

const VIEW_LOCATIONS = "locations";
const VIEW_DISCOUNTS = "discounts";
const VIEW_OPTIONS = [
  [VIEW_LOCATIONS, "Locations"],
  [VIEW_DISCOUNTS, "Discounts and comps"],
];

const DOWNLOAD_BUTTON_SX = {
  borderColor: "#6d28d9",
  color: "#6d28d9",
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#5b21b6", backgroundColor: "#f5f3ff" },
};

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

const count = (n) => Number(n || 0).toLocaleString("en-US");

const HEADER_CELL = "px-3 py-2 text-xs font-semibold text-slate-600 whitespace-nowrap";
const NUMBER_CELL = "px-3 py-2 text-sm text-right whitespace-nowrap";

function ReportTable({ children }) {
  return (
    <div
      tabIndex={0}
      role="region"
      aria-label="Scrollable report table"
      className="overflow-x-auto rounded-lg border border-slate-200 focus-visible:outline-violet-700"
    >
      <table className="min-w-full">{children}</table>
    </div>
  );
}

function LocationCells({ row, label, labelClassName }) {
  return (
    <>
      <td className={`px-3 py-2 text-sm ${labelClassName}`}>{label}</td>
      <td className={NUMBER_CELL}>{formatCurrency(row.net_sales)}</td>
      <td className={NUMBER_CELL}>{formatCurrency(row.discounts)}</td>
      <td className={NUMBER_CELL}>{formatCurrency(row.voids)}</td>
      <td className={NUMBER_CELL}>{count(row.voided_items)}</td>
      <td className={NUMBER_CELL}>{count(row.checks)}</td>
      <td className={NUMBER_CELL}>{formatCurrency(row.avg_check)}</td>
      <td className={NUMBER_CELL}>{count(row.guests)}</td>
      <td className={NUMBER_CELL}>{formatCurrency(row.avg_per_guest)}</td>
    </>
  );
}

function LocationTable({ rows, total }) {
  return (
    <ReportTable>
      <thead className="bg-slate-50">
        <tr>
          <th scope="col" className={`${HEADER_CELL} text-left`}>Location</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Net Sales</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Discounts</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>
            Voids
            <Info label="Voids">
              Items voided on this business day, at their price before discounts, including items from earlier orders.
            </Info>
          </th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Voided Items</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Checks</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Avg Check</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Guests</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>
            Avg per Guest
            <Info label="Avg per Guest">
              Net sales divided by guests, using only orders with a guest count.
            </Info>
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.store_id} className="text-slate-800">
            <LocationCells row={row} label={row.store_name} labelClassName="font-medium" />
          </tr>
        ))}
        {total && (
          <tr className="bg-violet-50/50 font-semibold text-violet-900">
            <LocationCells row={total} label="Total" labelClassName="" />
          </tr>
        )}
      </tbody>
    </ReportTable>
  );
}

function DiscountTable({ rows }) {
  return (
    <ReportTable>
      <thead className="bg-slate-50">
        <tr>
          <th scope="col" className={`${HEADER_CELL} text-left`}>Location</th>
          <th scope="col" className={`${HEADER_CELL} text-left`}>Discount</th>
          <th scope="col" className={`${HEADER_CELL} text-left`}>Reason</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Times Applied</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={`${row.store_id}-${row.discount}-${row.reason}`} className="text-slate-800">
            <td className="px-3 py-2 text-sm font-medium">{row.store_name}</td>
            <td className="px-3 py-2 text-sm">{row.discount}</td>
            <td className="px-3 py-2 text-sm text-slate-600">{row.reason || "–"}</td>
            <td className={NUMBER_CELL}>{count(row.count)}</td>
            <td className={NUMBER_CELL}>{formatCurrency(row.amount)}</td>
          </tr>
        ))}
      </tbody>
    </ReportTable>
  );
}

function EmptyMessage({ children }) {
  return (
    <div className="text-sm text-gray-500 py-12 text-center border border-slate-200 rounded-lg">
      {children}
    </div>
  );
}

export default function FlashReport({
  report,
  onBack,
  homeStore,
  homeCompany,
  storeNames,
  storeOptions: memberships,
}) {
  const scope = useCompanyReportScope({
    report,
    homeStore,
    homeCompany,
    storeNames,
    memberships,
  });
  const { companyId, anchorStoreId, storeFilter } = scope;

  // Until the user picks a date, show the selected company's last completed
  // business day. Derived in render, so a company switch moves the default.
  const [pickedDate, setPickedDate] = useState(null);
  const [view, setView] = useState(VIEW_LOCATIONS);
  const lastCompletedDay = lastCompletedBusinessDay(storeNames, anchorStoreId);
  const date = pickedDate ?? lastCompletedDay;

  const requestParams =
    homeStore && companyId
      ? {
          company_id: companyId,
          store_id: homeStore,
          date: dayjs(date).format("YYYY-MM-DD"),
          ...(storeFilter.storeIds ? { store_ids: storeFilter.storeIds } : {}),
        }
      : null;
  const { payload, isLoading, error, retry } = useScopedReportFetch(
    API_get_flash_report,
    requestParams,
    {
      onData: scope.acceptPayloadCompany,
      notFoundMessage: "You don't manage any Toast stores with this report.",
      errorMessage: "Couldn't load the flash report. Try again.",
    }
  );

  const incrementDay = useCallback((current, days) => {
    const next = new Date(current);
    next.setHours(0, 0, 0, 0);
    setPickedDate(addDays(next, days));
  }, []);

  const filters = (
    <div className="flex flex-wrap items-center gap-3">
      <DateSelector
        date={date}
        increment_day={incrementDay}
        // "Today" is the selected company's current business day.
        today={addDays(lastCompletedDay, 1)}
      />
      <ReportScopeFilters idPrefix="flash-report" payload={payload} scope={scope} />
    </div>
  );

  const locationRows = payload?.location_rows || [];
  const discountRows = payload?.discount_rows || [];
  const showingDiscounts = view === VIEW_DISCOUNTS;
  const hasViewRows = showingDiscounts ? discountRows.length > 0 : locationRows.length > 0;

  const actions = hasViewRows ? (
    <Button
      variant="outlined"
      size="small"
      startIcon={<FileDownloadOutlinedIcon />}
      onClick={() =>
        showingDiscounts
          ? downloadCSV(payload.discount_csv, `Flash_Report_Discounts_${payload.date}.csv`)
          : downloadCSV(payload.location_csv, `Flash_Report_Locations_${payload.date}.csv`)
      }
      className="prebuilt-report-no-print"
      sx={DOWNLOAD_BUTTON_SX}
    >
      Download CSV
    </Button>
  ) : null;

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      actions={actions}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={retry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : payload ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-600">
              Flash report for {dayjs(payload.date).format("dddd, MMMM D, YYYY")}
            </p>
            <div className="prebuilt-report-no-print">
              <Switch label="Report view" value={view} options={VIEW_OPTIONS} onChange={setView} />
            </div>
          </div>
          {showingDiscounts ? (
            discountRows.length ? (
              <DiscountTable rows={discountRows} />
            ) : (
              <EmptyMessage>No discounts or comps for this day.</EmptyMessage>
            )
          ) : locationRows.length ? (
            <LocationTable rows={locationRows} total={payload.total_row} />
          ) : (
            <EmptyMessage>No Toast sales for this day.</EmptyMessage>
          )}
        </div>
      ) : null}
    </PrebuiltReportShell>
  );
}
